import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { PrimaryButton } from './PrimaryButton';
import { FloatingLabelInput } from './FloatingLabelInput';
import { PhoneInput, emptyPhoneValue, getDialCode } from './PhoneInput';
import { ThemedSelect } from './ThemedSelect';
import { CmsModal } from './CmsModal';
import {
  getPublicServiceCategories,
  submitProfessionalApplication,
  type PublicServiceCategory,
} from '../../lib/professional-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

type CmsKind =
  | { code: 'TERMS_CONDITIONS'; title: 'Terms of Use' }
  | { code: 'PRIVACY_POLICY'; title: 'Privacy Policy' };

const CV_MAX_BYTES = 10 * 1024 * 1024;
const CV_ACCEPT = '.pdf,.doc,.docx';
const CV_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/**
 * Public "apply to join" form (mockup). This is a LEAD, not account creation —
 * approved applicants receive a tokenized invite link that takes them to
 * /professional/join/:token where the account is actually created.
 */
export function ProSignUpForm() {
  const [fullName, setFullName] = useState('');
  // Phone now carries a country so we can send dial_code. Defaults to GB
  // (+44) — the platform is UK-first.
  const [phone, setPhone] = useState(emptyPhoneValue('GB'));
  const [email, setEmail] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [cv, setCv] = useState<File | null>(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const [categories, setCategories] = useState<PublicServiceCategory[]>([]);
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeCms, setActiveCms] = useState<CmsKind | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    getPublicServiceCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const pickCv = (file: File | null) => {
    if (!file) return;
    if (!CV_MIME.includes(file.type)) {
      setErrors((e) => ({ ...e, cv: 'CV must be a PDF or Word document.' }));
      return;
    }
    if (file.size > CV_MAX_BYTES) {
      setErrors((e) => ({ ...e, cv: 'CV must be 10 MB or smaller.' }));
      return;
    }
    setErrors((e) => ({ ...e, cv: null }));
    setCv(file);
  };

  const handleSubmit = async () => {
    const next: Record<string, string | null> = {
      fullName: fullName.trim() ? null : 'Please enter your full name.',
      phone: phone.mobileNumber.trim()
        ? null
        : 'Please enter your phone number.',
      email: validateEmail(email) ? null : 'Please enter a valid email address.',
      category: categoryId ? null : 'Please select a category.',
      cv: cv ? null : 'Please attach your CV (PDF or Word).',
      terms: agreeToTerms ? null : 'You must agree to continue.',
    };
    setErrors(next);
    if (Object.values(next).some(Boolean)) return;

    setSubmitting(true);
    try {
      await submitProfessionalApplication({
        full_name: fullName.trim(),
        email: email.trim().toLowerCase(),
        dial_code: getDialCode(phone.country),
        mobile_number: phone.mobileNumber.trim(),
        service_category_id: categoryId || undefined,
        accept_terms: true,
        cv: cv as File,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not submit your application.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="w-full max-w-[420px] mx-auto flex flex-col items-center gap-6 px-4 text-center">
        <div className="size-[64px] rounded-full bg-[#8e2a2f]/[0.08] flex items-center justify-center">
          <span className="material-icons text-[#8e2a2f] text-[32px]">
            check_circle
          </span>
        </div>
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px] leading-[36px]">
          Application received
        </h1>
        <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[15px] leading-[24px]">
          Thank you for applying to Cherry Picked. We aim to contact you within 5
          working days if we think you could be a good fit. Please check your
          spam or junk folder in case our reply lands there.
        </p>
        <Link
          to="/"
          className="font-['DM_Sans',sans-serif] text-[#8e2a2f] font-bold hover:underline text-[14px]"
        >
          Back to home
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[420px] mx-auto flex flex-col items-center gap-6 px-4">
      <div className="text-center w-full">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold leading-[40px] text-[#8e2a2f] text-[30px] tracking-[-0.0944px] mb-1">
          Apply to join Cherry Picked
        </h1>
        <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
          Tell us a bit about yourself and we will be in touch
        </p>
      </div>

      <div className="w-full flex flex-col gap-5">
        <FloatingLabelInput
          label="Full name"
          type="text"
          value={fullName}
          onChange={(v) => {
            setFullName(v);
            setErrors((e) => ({ ...e, fullName: null }));
          }}
          icon={``}
          className="w-full"
          error={!!errors.fullName}
          errorMessage={errors.fullName ?? ''}
        />

        <PhoneInput
          label="Phone number"
          value={phone}
          onChange={(next) => {
            setPhone(next);
            setErrors((e) => ({ ...e, phone: null }));
          }}
          error={errors.phone ?? null}
          placeholder="7700900000"
        />

        <FloatingLabelInput
          label="Email address"
          type="email"
          value={email}
          onChange={(v) => {
            setEmail(v);
            setErrors((e) => ({ ...e, email: null }));
          }}
          icon={``}
          className="w-full"
          error={!!errors.email}
          errorMessage={errors.email ?? ''}
        />

        {/* Category */}
        <div className="flex flex-col gap-1">
          <label className="font-['DM_Sans',sans-serif] text-[13px] font-bold text-[#3a3335]">
            Which category are you applying for?
          </label>
          <ThemedSelect
            value={categoryId}
            onChange={(v) => {
              setCategoryId(v);
              setErrors((e) => ({ ...e, category: null }));
            }}
            options={[
              { value: '', label: 'Select a category' },
              ...categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
            className="w-full"
            buttonClassName={`w-full h-[52px] ${
              errors.category ? '!border-red-400' : ''
            }`}
          />
          {errors.category && (
            <p className="font-['DM_Sans',sans-serif] text-[12px] text-red-500">
              {errors.category}
            </p>
          )}
        </div>

        {/* CV upload */}
        <div className="flex flex-col gap-1">
          <label className="font-['DM_Sans',sans-serif] text-[13px] font-bold text-[#3a3335]">
            Upload your CV
          </label>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickCv(e.dataTransfer.files?.[0] ?? null);
            }}
            className={`w-full rounded-[10px] border border-dashed px-4 py-6 text-center transition-colors ${
              errors.cv ? 'border-red-400' : 'border-[#d9c9cb] hover:border-[#8e2a2f]'
            }`}
          >
            <span className="material-icons text-[#8e2a2f] text-[22px]">
              upload
            </span>
            {cv ? (
              <p className="font-['DM_Sans',sans-serif] text-[14px] text-[#3a3335] mt-1 truncate">
                {cv.name}
              </p>
            ) : (
              <>
                <p className="font-['DM_Sans',sans-serif] text-[14px] text-[#908e8e] mt-1">
                  Drop your CV here or{' '}
                  <span className="text-[#8e2a2f] underline">browse files</span>
                </p>
                <p className="font-['DM_Sans',sans-serif] text-[11px] text-[#b3b0b0] mt-0.5">
                  PDF or Word document, up to 10 MB
                </p>
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept={CV_ACCEPT}
            className="hidden"
            onChange={(e) => pickCv(e.target.files?.[0] ?? null)}
          />
          {errors.cv && (
            <p className="font-['DM_Sans',sans-serif] text-[12px] text-red-500">
              {errors.cv}
            </p>
          )}
        </div>

        {/* Terms */}
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-[10px]">
            <button
              type="button"
              aria-label="Agree to terms"
              onClick={() => {
                setAgreeToTerms(!agreeToTerms);
                setErrors((e) => ({ ...e, terms: null }));
              }}
              className={`mt-1 size-[20px] rounded-full flex items-center justify-center transition-colors border shrink-0 ${
                agreeToTerms
                  ? 'bg-[#8e2a2f] border-[#8e2a2f]'
                  : `bg-white ${errors.terms ? 'border-red-500' : 'border-[#eee]'}`
              }`}
            >
              {agreeToTerms && <span className="text-white text-[12px]">✓</span>}
            </button>
            <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              I agree with the{' '}
              <button
                type="button"
                onClick={() =>
                  setActiveCms({ code: 'TERMS_CONDITIONS', title: 'Terms of Use' })
                }
                className="text-[#8e2a2f] font-bold underline hover:no-underline"
              >
                Terms of Use
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() =>
                  setActiveCms({ code: 'PRIVACY_POLICY', title: 'Privacy Policy' })
                }
                className="text-[#8e2a2f] font-bold underline hover:no-underline"
              >
                Privacy Policy
              </button>
            </p>
          </div>
          {errors.terms && (
            <p className="font-['DM_Sans',sans-serif] text-[12px] text-red-500 pl-[30px]">
              {errors.terms}
            </p>
          )}
        </div>

        <PrimaryButton
          onClick={handleSubmit}
          className="w-full"
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : 'Submit application'}
        </PrimaryButton>
      </div>

      <div className="text-center mt-2">
        <p className="font-['DM_Sans',sans-serif] text-[14px] text-[#908e8e]">
          Already have a professional account?{' '}
          <Link
            to="/professional/signin"
            className="text-[#8e2a2f] font-bold hover:underline"
          >
            Sign In
          </Link>
        </p>
      </div>

      <CmsModal
        isOpen={!!activeCms}
        onClose={() => setActiveCms(null)}
        code={activeCms?.code ?? 'TERMS_CONDITIONS'}
        userType="PROFESSIONAL"
        fallbackTitle={activeCms?.title}
      />
    </div>
  );
}
